import ApiService from "@/core/services/api.service";
import moment from "moment-timezone";
import {
    EVENT_STATUS,
    EVENT_TYPE,
    RECURRENCE_SCOPE,
    isFixedScheduleExpired,
} from "@/core/config/teamLeadsCalendar/teamLeadsCalendarOption";

/**
 * Store module cho trang "Lịch làm việc Teamlead".
 *
 * Viết theo đúng format của `user/users.module.js`: hằng số action mang giá trị
 * là endpoint, thân action chỉ là `new Promise` bọc `ApiService`, resolve `data`
 * hoặc reject `response` để component tự hiện message. Không còn fake data.
 *
 * Endpoint backend ĐÃ CÓ:
 *   GET    teamlead/schedules          ?start_date,end_date,user_id
 *   POST   teamlead/schedules          {user_id, task, start_time, end_time, start_date}
 *   PUT    teamlead/schedules/{id}
 *   DELETE teamlead/schedules/{id}
 *   GET    teamlead/fixed-schedules    ?page,limit,user_id
 *   POST   teamlead/fixed-schedules    {user_id, task, date_of_week[], start_time, end_time, start_date, end_date}
 *   PUT    teamlead/fixed-schedules/{id}
 *   DELETE teamlead/fixed-schedules/{id}
 *
 * Endpoint backend CHƯA CÓ (gọi sẽ lỗi cho tới khi BE bổ sung) — giữ đúng
 * contract mà FE đang chờ:
 *   GET    team-leads-calendar/team-leads
 *   GET/POST/PUT/DELETE team-leads-calendar/events
 *   POST   team-leads-calendar/events/{id}/status
 *   POST   team-leads-calendar/events/{id}/respond
 *   GET    team-leads-calendar/free-busy
 *   GET    team-leads-calendar/reminders
 *   POST   team-leads-calendar/reminders/{id}/read
 */

/** BE trả `date` theo UTC (vd 2026-09-06T17:00:00Z = ngày 07/09 giờ VN) */
const TZ = "Asia/Ho_Chi_Minh";

//action types
export const TLC_GET_SCHEDULES = "teamlead/schedules";
export const TLC_CREATE_SCHEDULE = "create-teamlead-schedule";
export const TLC_UPDATE_SCHEDULE = "update-teamlead-schedule";
export const TLC_DELETE_SCHEDULE = "delete-teamlead-schedule";

export const TLC_GET_FIXED_SCHEDULES = "teamlead/fixed-schedules";
export const TLC_CREATE_FIXED_SCHEDULE = "create-teamlead-fixed-schedule";
export const TLC_UPDATE_FIXED_SCHEDULE = "update-teamlead-fixed-schedule";
export const TLC_DELETE_FIXED_SCHEDULE = "delete-teamlead-fixed-schedule";

export const TLC_GET_TEAM_LEADS = "team-leads-calendar/team-leads";
export const TLC_GET_EVENTS = "team-leads-calendar/events";
export const TLC_CREATE_EVENT = "create-team-leads-calendar-event";
export const TLC_UPDATE_EVENT = "update-team-leads-calendar-event";
export const TLC_DELETE_EVENT = "delete-team-leads-calendar-event";
export const TLC_UPDATE_EVENT_STATUS = "team-leads-calendar/event-status";
export const TLC_RESPOND_MEETING = "team-leads-calendar/event-respond";

export const TLC_GET_FREE_BUSY = "team-leads-calendar/free-busy";
export const TLC_GET_REMINDERS = "team-leads-calendar/reminders";
export const TLC_MARK_REMINDER_READ = "team-leads-calendar/reminder-read";

//mutations types
export const TLC_SET_TEAM_LEADS = "tlcSetTeamLeads";
export const TLC_SET_EVENTS = "tlcSetEvents";
export const TLC_SET_REMINDERS = "tlcSetReminders";
export const TLC_SET_LOADING = "tlcSetLoading";
export const TLC_SET_VIEW_ALL = "tlcSetViewAll";

const state = {
    teamLeads: [],
    events: [],
    reminders: [],
    /** null = chưa gọi API; true = xem lịch của mọi Teamlead */
    viewAll: null,
    loading: false,
};

const getters = {
    tlcTeamLeads(state) {
        return state.teamLeads;
    },
    tlcEvents(state) {
        return state.events;
    },
    tlcReminders(state) {
        return state.reminders;
    },
    tlcViewAll(state) {
        return state.viewAll;
    },
    tlcLoading(state) {
        return state.loading;
    },
    tlcUnreadReminderCount(state) {
        return state.reminders.filter((r) => !r.is_read).length;
    },
};

/* ------------------------------------------------------------------ *
 * Chuẩn hoá response của BE về đúng shape các trang đang dùng
 * ------------------------------------------------------------------ */

/** "08:00:00" -> "08:00"; rỗng/không hợp lệ -> null */
function toHourMinute(value) {
    const m = value ? String(value).match(/^(\d{1,2}):(\d{2})/) : null;
    return m ? `${m[1].padStart(2, "0")}:${m[2]}` : null;
}

/** ISO/UTC hoặc "YYYY-MM-DD" -> ngày theo giờ VN */
function toLocalDate(value) {
    if (!value) return null;
    const m = moment.tz(value, TZ);
    return m.isValid() ? m.format("YYYY-MM-DD") : null;
}

/** 1 bản ghi `schedules[]` -> event dùng chung cho lưới tuần / panel ngày */
function normalizeSchedule(row, owner) {
    const start = toHourMinute(row.start_time);
    const user = row.user || owner || {};
    const createdBy = row.created_by;
    // created_user = 0/null: Teamlead tự đặt; khác user_id: người khác (HR) đặt hộ -> khoá sửa
    const byOther = !!(row.created_user && Number(row.created_user) !== Number(row.user_id));
    return {
        id: row.id,
        user_id: row.user_id != null ? row.user_id : user.id,
        team_lead_id: row.user_id != null ? row.user_id : user.id,
        team_lead_name: user.name || "",
        type: EVENT_TYPE.TASK,
        title: row.task || "",
        task: row.task || "",
        description: "",
        date: toLocalDate(row.date),
        start_time: start,
        end_time: toHourMinute(row.end_time),
        location: "",
        meeting_link: "",
        status: EVENT_STATUS.SCHEDULED,
        source: row.recruitment_interview_id ? "interview" : (byOther ? "hr" : "self"),
        created_by_name: (createdBy && typeof createdBy === "object" ? createdBy.name : createdBy) || "",
        recurrence_id: null,
        is_locked: byOther || !!row.recruitment_interview_id,
        remind_before: 0,
        raw: row,
    };
}

/** Gom Teamlead (không trùng id) từ object `user` mà BE trả kèm mỗi bản ghi */
function pushTeamLead(list, user, fallbackId, extra = {}) {
    const owner = user || {};
    const id = owner.id != null ? owner.id : fallbackId;
    if (id == null || list.some((t) => String(t.id) === String(id))) return;
    const { schedules, user: u, ...restExtra } = extra;
    list.push({
        ...restExtra,
        ...owner,
        id,
        name: owner.name || `#${id}`,
        email: owner.email || restExtra.email || "",
        image: owner.image || restExtra.image || "",
        team_name: owner.team_name || restExtra.team_name || "",
        branch_name: owner.branch_name || restExtra.branch_name || "",
    });
}

/**
 * Bóc tách response `teamlead/schedules` thành { viewAll, currentUserId, teamLeads, events }.
 * `from`/`to` (YYYY-MM-DD) dùng để cắt về đúng tuần đang xem, phòng khi BE trả toàn bộ.
 * Nhận cả 2 dạng `data.data`: mảng phẳng các bản ghi lịch và dạng gom nhóm
 * [{ user_id, user, schedules: [] }] (dạng backend trả về theo cấu trúc mới).
 */
export function normalizeSchedulePayload(body, { from, to } = {}) {
    const wrap = (body && body.data) || {};
    const rows = Array.isArray(wrap.data) ? wrap.data : Array.isArray(wrap) ? wrap : [];
    const teamLeads = [];
    const events = [];

    const collect = (row, owner) => {
        if (!row || row.deleted_at) return;
        const ev = normalizeSchedule(row, owner);
        if (!ev.date) return;
        if (from && ev.date < from) return;
        if (to && ev.date > to) return;
        events.push(ev);
    };

    rows.forEach((row) => {
        if (!row) return;
        if (Array.isArray(row.schedules)) {
            // Dạng BE: mỗi phần tử là 1 Teamlead kèm danh sách lịch
            const owner = row.user || {};
            pushTeamLead(teamLeads, owner, row.user_id, row);
            row.schedules.forEach((item) => collect(item, owner));
            return;
        }
        // Dạng phẳng: mỗi phần tử là 1 bản ghi lịch, Teamlead nằm trong `user`
        if (row.deleted_at) return;
        pushTeamLead(teamLeads, row.user, row.user_id);
        collect(row, row.user);
    });

    return {
        viewAll: wrap.view_all == null ? null : !!wrap.view_all,
        // `user_id` ở tầng wrap = tài khoản đang đăng nhập (BE dùng để giới hạn phạm vi)
        currentUserId: wrap.user_id != null ? wrap.user_id : null,
        teamLeads,
        events,
    };
}

/** 1 bản ghi `teamlead/fixed-schedules` -> dòng hiển thị ở trang quản lý lịch cố định */
function normalizeFixedSchedule(row) {
    const start = toHourMinute(row.start_time);
    const end = toHourMinute(row.end_time);
    const user = row.user || {};
    const createdBy = row.created_by;
    return {
        id: row.id,
        // BE gắn lịch cố định theo từng user nên luôn là phạm vi cá nhân
        scope: RECURRENCE_SCOPE.INDIVIDUAL,
        department_id: null,
        team_name: null,
        team_lead_id: row.user_id != null ? row.user_id : user.id,
        team_lead_name: user.name || "",
        type: EVENT_TYPE.TASK,
        title: row.task || "",
        description: "",
        // `date_of_week` dùng chuẩn ISO giống WEEKDAYS: 1 = Thứ 2 ... 7 = Chủ nhật
        slots: (row.date_of_week || []).map((weekday) => ({
            weekday: Number(weekday),
            start_time: start,
            end_time: end,
        })),
        weekday_names: row.date_of_week_names || [],
        effective_from: row.start_date || null,
        // `end_date` = null nghĩa là lịch lặp không giới hạn
        effective_to: row.end_date || null,
        remind_before: 0,
        // created_user khác user_id: người khác (HR) đặt hộ -> Teamlead không tự sửa
        is_locked: !!(row.created_user && Number(row.created_user) !== Number(row.user_id)),
        // Hết phạm vi áp dụng thì không còn sinh lịch cho các tuần tiếp theo
        active: !isFixedScheduleExpired({ effective_to: row.end_date || null }),
        created_by_name: (createdBy && typeof createdBy === "object" ? createdBy.name : createdBy) || "",
        raw: row,
    };
}

/** Bóc tách response `teamlead/fixed-schedules` (nhận cả dạng paginator lẫn mảng thuần) */
export function normalizeFixedSchedulePayload(body) {
    const wrap = (body && body.data) || {};
    const rows = Array.isArray(wrap.data) ? wrap.data : Array.isArray(wrap) ? wrap : [];
    return rows.filter((row) => row && !row.deleted_at).map(normalizeFixedSchedule);
}

/** Meta phân trang của Laravel paginator (mặc định an toàn khi BE trả mảng thuần) */
export function fixedSchedulePaging(body) {
    const wrap = (body && body.data) || {};
    const rows = Array.isArray(wrap.data) ? wrap.data : Array.isArray(wrap) ? wrap : [];
    return {
        current_page: Number(wrap.current_page) || 1,
        per_page: Number(wrap.per_page) || rows.length || 20,
        total: Number(wrap.total) || rows.length,
        last_page: Number(wrap.last_page) || 1,
    };
}

const actions = {
    /* ---------------------------------------------------------------- *
     * Lịch làm việc theo ngày
     * ---------------------------------------------------------------- */
    /**
     * payload: { date_range, user_id? } hoặc { date, user_id? }
     * BE yêu cầu dùng `date_range` thay cho `date`.
     * Chỉ ghi đè danh sách Teamlead khi đang xem tất cả (không lọc theo user_id),
     * để việc chọn 1 Teamlead ở sidebar/select không làm mất các Teamlead còn lại.
     */
    [TLC_GET_SCHEDULES](context, payload) {
        context.commit(TLC_SET_LOADING, true);
        const params = { ...payload };

        // Chuẩn hoá filter sang date_range theo contract mới của backend
        if (!params.date_range) {
            if (params.date) {
                params.date_range = [params.date, params.date];
                delete params.date;
            } else if (params.date_from && params.date_to) {
                params.date_range = [params.date_from, params.date_to];
            } else if (params.start_date && params.end_date) {
                params.date_range = [params.start_date, params.end_date];
            }
        }

        return new Promise((resolve, reject) => {
            ApiService.query(TLC_GET_SCHEDULES, params).then(({ data }) => {
                let targetFrom = params.date_from || params.start_date || params.from;
                let targetTo = params.date_to || params.end_date || params.to;
                if (Array.isArray(params.date_range) && params.date_range.length >= 2) {
                    if (!targetFrom) targetFrom = params.date_range[0];
                    if (!targetTo) targetTo = params.date_range[1];
                } else if (params.date) {
                    if (!targetFrom) targetFrom = params.date;
                    if (!targetTo) targetTo = params.date;
                }

                const { viewAll, teamLeads, events } = normalizeSchedulePayload(data, {
                    from: targetFrom,
                    to: targetTo,
                });
                context.commit(TLC_SET_VIEW_ALL, viewAll);
                context.commit(TLC_SET_EVENTS, events);
                // view_all = false: BE chỉ trả lịch của chính tài khoản đang đăng nhập
                // nên danh sách suy ra luôn đúng, kể cả khi request có lọc `user_id`.
                if (teamLeads.length && (viewAll === false || !context.state.teamLeads.length || !(payload && payload.user_id))) {
                    context.commit(TLC_SET_TEAM_LEADS, teamLeads);
                }
                context.commit(TLC_SET_LOADING, false);
                resolve(data);
            }).catch(({ response }) => {
                context.commit(TLC_SET_EVENTS, []);
                context.commit(TLC_SET_LOADING, false);
                reject(response);
            });
        });
    },

    [TLC_CREATE_SCHEDULE](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post("teamlead/schedules", payload).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_UPDATE_SCHEDULE](context, payload) {
        const { id, ...body } = payload;
        return new Promise((resolve, reject) => {
            ApiService.update("teamlead/schedules", id, body).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_DELETE_SCHEDULE](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        return new Promise((resolve, reject) => {
            // ApiService.delete bọc lỗi thành Error thường nên không còn `response`
            ApiService.delete("teamlead/schedules", id).then(({ data }) => {
                resolve(data);
            }).catch((error) => {
                reject(error);
            });
        });
    },

    /* ---------------------------------------------------------------- *
     * Lịch cố định (lặp theo thứ) — trang "Quản lý lịch cố định Teamlead"
     * ---------------------------------------------------------------- */
    /** Resolve { rows, paging } — không commit vào store vì chỉ trang quản lý dùng tới */
    [TLC_GET_FIXED_SCHEDULES](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.query(TLC_GET_FIXED_SCHEDULES, payload).then(({ data }) => {
                resolve({ rows: normalizeFixedSchedulePayload(data), paging: fixedSchedulePaging(data) });
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_CREATE_FIXED_SCHEDULE](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post("teamlead/fixed-schedules", payload).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_UPDATE_FIXED_SCHEDULE](context, payload) {
        const { id, ...body } = payload;
        return new Promise((resolve, reject) => {
            ApiService.update("teamlead/fixed-schedules", id, body).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_DELETE_FIXED_SCHEDULE](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        return new Promise((resolve, reject) => {
            // ApiService.delete bọc lỗi thành Error thường nên không còn `response`
            ApiService.delete("teamlead/fixed-schedules", id).then(({ data }) => {
                resolve(data);
            }).catch((error) => {
                reject(error);
            });
        });
    },

    /* ---------------------------------------------------------------- *
     * Sự kiện lịch — BE chưa có endpoint
     * ---------------------------------------------------------------- */
    [TLC_GET_TEAM_LEADS](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.query(TLC_GET_TEAM_LEADS, payload).then(({ data }) => {
                context.commit(TLC_SET_TEAM_LEADS, data.data || []);
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_GET_EVENTS](context, payload) {
        context.commit(TLC_SET_LOADING, true);
        return new Promise((resolve, reject) => {
            ApiService.query(TLC_GET_EVENTS, payload).then(({ data }) => {
                context.commit(TLC_SET_EVENTS, data.data || []);
                context.commit(TLC_SET_LOADING, false);
                resolve(data);
            }).catch(({ response }) => {
                context.commit(TLC_SET_LOADING, false);
                reject(response);
            });
        });
    },

    [TLC_CREATE_EVENT](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post("team-leads-calendar/events", payload).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_UPDATE_EVENT](context, payload) {
        const { id, ...body } = payload;
        return new Promise((resolve, reject) => {
            ApiService.update("team-leads-calendar/events", id, body).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_DELETE_EVENT](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        const scope = typeof payload === "object" ? payload.scope : undefined;
        return new Promise((resolve, reject) => {
            // ApiService.delete bọc lỗi thành Error thường nên không còn `response`
            ApiService.delete("team-leads-calendar/events", scope ? `${id}?scope=${scope}` : id).then(({ data }) => {
                resolve(data);
            }).catch((error) => {
                reject(error);
            });
        });
    },

    [TLC_UPDATE_EVENT_STATUS](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`team-leads-calendar/events/${payload.id}/status`, { status: payload.status })
                .then(({ data }) => {
                    resolve(data);
                }).catch(({ response }) => {
                    reject(response);
                });
        });
    },

    [TLC_RESPOND_MEETING](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.post(`team-leads-calendar/events/${payload.id}/respond`, {
                response: payload.response,
                reason: payload.reason,
            }).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    /* ---------------------------------------------------------------- *
     * Lịch rảnh & nhắc việc — BE chưa có endpoint
     * ---------------------------------------------------------------- */
    [TLC_GET_FREE_BUSY](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.query(TLC_GET_FREE_BUSY, payload).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },

    [TLC_GET_REMINDERS](context, payload) {
        return new Promise((resolve, reject) => {
            ApiService.query(TLC_GET_REMINDERS, payload).then(({ data }) => {
                context.commit(TLC_SET_REMINDERS, data.data || []);
                resolve(data);
            }).catch(({ response }) => {
                context.commit(TLC_SET_REMINDERS, []);
                reject(response);
            });
        });
    },

    [TLC_MARK_REMINDER_READ](context, payload) {
        const id = typeof payload === "object" ? payload.id : payload;
        return new Promise((resolve, reject) => {
            ApiService.post(`team-leads-calendar/reminders/${id}/read`, {}).then(({ data }) => {
                resolve(data);
            }).catch(({ response }) => {
                reject(response);
            });
        });
    },
};

const mutations = {
    [TLC_SET_TEAM_LEADS](state, data) {
        state.teamLeads = Array.isArray(data) ? data : [];
    },
    [TLC_SET_EVENTS](state, data) {
        state.events = Array.isArray(data) ? data : [];
    },
    [TLC_SET_REMINDERS](state, data) {
        state.reminders = Array.isArray(data) ? data : [];
    },
    [TLC_SET_VIEW_ALL](state, value) {
        state.viewAll = value == null ? null : !!value;
    },
    [TLC_SET_LOADING](state, value) {
        state.loading = !!value;
    },
};

export default {
    state,
    actions,
    mutations,
    getters,
};
